import { useEffect, useRef, useState } from 'react';

/** Below this, a request is fast enough that a spinner would only blink. */
export const BUSY_SHOW_DELAY_MS = 250;
/** Once shown, it stays at least this long, so it reads as a state and not a flicker. */
export const BUSY_MIN_VISIBLE_MS = 600;

/**
 * Whether a loading indicator should be ON, given whether the real work is `active`.
 *
 * It never follows a timer on its own — `active` is the request itself — but it smooths
 * the edges the product owner asked about: a fetch that answers in ~150 ms never shows a
 * spinner at all, and one that does show stays long enough to be read rather than flash.
 * When the work ends it goes off (after the minimum), whatever the outcome.
 */
export function useDelayedBusy(active: boolean, showDelayMs = BUSY_SHOW_DELAY_MS, minVisibleMs = BUSY_MIN_VISIBLE_MS): boolean {
    const [visible, setVisible] = useState(false);
    const shownAt = useRef<number | null>(null);

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout> | undefined;
        if (active && !visible) {
            timer = setTimeout(() => { shownAt.current = Date.now(); setVisible(true); }, showDelayMs);
        } else if (!active && visible) {
            const left = Math.max(0, (shownAt.current ?? 0) + minVisibleMs - Date.now());
            timer = setTimeout(() => { shownAt.current = null; setVisible(false); }, left);
        }
        return () => { if (timer) clearTimeout(timer); };
    }, [active, visible, showDelayMs, minVisibleMs]);

    return visible;
}
